import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, Check } from 'lucide-react';
import { unifiedControlStyle } from './ReusableDropdown';

const ReusableMultiSelect = ({
  items = [],
  selectedIds = [],
  onChange,
  placeholder = 'Select...',
  emptyMessage = 'No results found',
  maxItems = 8,
  getItemKey = (item) => item.id || item.email,
  getItemLabel = (item) => item.name || item.email,
  filterFunction,
  renderItem,
  theme = 'light',
  language = 'en',
  disabled = false,
  inputStyle = {},
  dropdownStyle = {},
  itemStyle = {},
  showAllOnArrowClick = true,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showingAll, setShowingAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalSelectedIds, setInternalSelectedIds] = useState(selectedIds || []);

  // Sync internal state when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      setInternalSelectedIds(selectedIds || []);
    }
  }, [showDropdown, selectedIds]);

  const handleCloseDropdown = () => {
    setShowDropdown(false);
    setSearchTerm('');
    setShowingAll(false);
    // Fire onChange with the final selections when closing
    if (JSON.stringify(internalSelectedIds) !== JSON.stringify(selectedIds)) {
      onChange(internalSelectedIds);
    }
  };
  
  const containerRef = useRef(null);
  const portalRef = useRef(null);
  const inputRef = useRef(null);
  
  const [dropdownCoords, setDropdownCoords] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const isRTL = language === 'ar';
  const isDark = theme === 'dark';

  const defaultFilterFunction = (item, search) => {
    const label = String(getItemLabel(item) || '').toLowerCase();
    const s = String(search || '').toLowerCase();
    return label.includes(s);
  };

  useEffect(() => {
    if (showingAll) {
      setFilteredItems(items || []);
      setHighlightedIndex(-1);
      return;
    }
    if (!String(searchTerm || '').trim()) {
      setFilteredItems((items || []).slice(0, maxItems));
      setHighlightedIndex(-1);
      return;
    }
    const filterFunc = filterFunction || defaultFilterFunction;
    const filtered = (items || [])
      .filter(item => filterFunc(item, searchTerm))
      .slice(0, maxItems);
    setFilteredItems(filtered);
    setHighlightedIndex(-1);
  }, [searchTerm, items, maxItems, showingAll, filterFunction, getItemLabel]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const container = containerRef.current;
      const portal = portalRef.current;
      if (
        container && !container.contains(event.target) &&
        (!portal || !portal.contains(event.target))
      ) {
        handleCloseDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // Re-bind on every selection change — an empty dep array here would
    // permanently capture the mount-time (empty) internalSelectedIds in the
    // closure, so an outside click would always commit an empty selection
    // no matter what the user actually checked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalSelectedIds, selectedIds, onChange]);

  const updateDropdownCoords = () => {
    const inputEl = containerRef.current;
    if (!inputEl) return;
    const rect = inputEl.getBoundingClientRect();
    const headerHeight = 48;
    const itemHeight = 44;
    const maxHeight = 300;
    const estimatedHeight = Math.min(maxHeight, headerHeight + (filteredItems.length * itemHeight));
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    setDropdownDirection(openUp ? 'up' : 'down');
    setDropdownCoords({
      top: openUp ? (rect.top - estimatedHeight) : rect.bottom,
      left: rect.left,
      width: rect.width,
      height: estimatedHeight,
    });
  };

  useEffect(() => {
    if (!showDropdown) return;
    updateDropdownCoords();
    const handler = () => updateDropdownCoords();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [showDropdown, showingAll, filteredItems.length]);

  const toggleSelection = (itemId, e) => {
    if (e) e.stopPropagation();
    setInternalSelectedIds(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && !searchTerm && internalSelectedIds.length > 0) {
      e.preventDefault();
      setInternalSelectedIds(prev => prev.slice(0, -1));
      return;
    }
    if (!showDropdown || filteredItems.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filteredItems.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredItems.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          toggleSelection(getItemKey(filteredItems[highlightedIndex]));
        }
        break;
      case 'Escape':
        handleCloseDropdown();
        break;
      default:
        break;
    }
  };

  const baseInputContainerStyle = {
    ...unifiedControlStyle(theme, isRTL),
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '6px 48px 6px 12px',
    paddingRight: isRTL ? '12px' : '48px',
    paddingLeft: isRTL ? '48px' : '12px',
    cursor: 'text',
    minHeight: '48px',
    ...inputStyle,
  };

  const chipStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: 500,
    padding: '4px 8px',
    borderRadius: '6px',
    color: isDark ? '#93c5fd' : '#1d4ed8',
    background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)',
    border: `1px solid ${isDark ? 'rgba(96,165,250,0.3)' : 'rgba(37,99,235,0.2)'}`,
  };

  const baseDropdownStyle = {
    position: 'absolute',
    background: isDark ? '#0f172a' : '#ffffff',
    border: isDark ? '1px solid rgba(148, 163, 184, 0.24)' : '1px solid rgba(148, 163, 184, 0.3)',
    boxShadow: isDark ? '0 20px 40px rgba(15, 23, 42, 0.35)' : '0 18px 30px rgba(15, 23, 42, 0.08)',
    borderRadius: '12px',
    zIndex: 99999,
    maxHeight: '300px',
    overflowY: 'auto',
    direction: isRTL ? 'rtl' : 'ltr',
    ...dropdownStyle,
  };

  const baseItemStyle = {
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    fontSize: '14px',
    color: isDark ? '#f1f5f9' : '#334155',
    borderBottom: isDark ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid #f3f4f6',
    transition: 'all 0.2s ease',
    flexDirection: isRTL ? 'row-reverse' : 'row',
    ...itemStyle,
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div 
        style={baseInputContainerStyle} 
        onClick={() => {
          if (disabled) return;
          setShowDropdown(true);
          setShowingAll(true);
          if (inputRef.current) inputRef.current.focus();
        }}
      >
        {internalSelectedIds.map(id => {
          const item = items.find(i => getItemKey(i) === id);
          if (!item) return null;
          return (
            <span key={id} style={chipStyle} onClick={(e) => e.stopPropagation()}>
              {getItemLabel(item)}
              <X size={12} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={(e) => toggleSelection(id, e)} />
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowingAll(false);
            setShowDropdown(true);
          }}
          onFocus={() => {
            if (!showDropdown) {
              setShowDropdown(true);
              setShowingAll(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={internalSelectedIds.length === 0 ? placeholder : ''}
          disabled={disabled}
          autoComplete="off"
          style={{
            border: 'none',
            background: 'transparent',
            outline: 'none',
            color: isDark ? '#f1f5f9' : '#334155',
            fontSize: '14px',
            minWidth: '50px',
            flex: 1,
          }}
        />

        <div style={{
          position: 'absolute',
          left: isRTL ? '8px' : 'auto',
          right: isRTL ? 'auto' : '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
        }}>
          {showAllOnArrowClick && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                if (showDropdown) {
                  handleCloseDropdown();
                } else {
                  setShowingAll(true);
                  setShowDropdown(true);
                  if (inputRef.current) inputRef.current.focus();
                }
              }}
              disabled={disabled}
              style={{
                background: 'none',
                border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronDown size={18} />
            </button>
          )}
        </div>
      </div>

      {showDropdown && filteredItems.length > 0 && (
        (typeof document !== 'undefined' && dropdownCoords)
          ? createPortal(
            <div
              className="reusable-dropdown-portal"
              ref={portalRef}
              style={{
                position: 'fixed',
                top: dropdownCoords.top + 'px',
                left: dropdownCoords.left + 'px',
                width: dropdownCoords.width + 'px',
                marginTop: dropdownDirection === 'down' ? '4px' : 0,
                ...baseDropdownStyle,
              }}
            >
              <div style={{
                padding: '12px 16px',
                borderBottom: isDark ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid #f3f4f6',
                fontSize: '12px',
                color: isDark ? 'rgba(203, 213, 225, 0.6)' : '#6b7280',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textAlign: isRTL ? 'right' : 'left',
              }}>
                {filteredItems.length} {language === 'ar' ? 'نتيجة' : 'results'}
              </div>

              {filteredItems.map((item, index) => {
                const isHighlighted = index === highlightedIndex;
                const itemKey = getItemKey(item);
                const isSelected = internalSelectedIds.includes(itemKey);
                
                return (
                  <div
                    key={itemKey}
                    style={{ 
                      ...baseItemStyle, 
                      background: isHighlighted 
                        ? (isDark ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)') 
                        : (isSelected ? (isDark ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.04)') : 'transparent') 
                    }}
                    onClick={(e) => toggleSelection(itemKey, e)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {!isRTL && (
                      <input 
                        type="checkbox" 
                        checked={isSelected} 
                        readOnly 
                        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6', flexShrink: 0 }} 
                      />
                    )}
                    <div style={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      <div style={{ fontWeight: isSelected ? 600 : 500 }}>
                        {renderItem ? renderItem(item, theme, language) : getItemLabel(item)}
                      </div>
                    </div>
                    {isRTL && (
                      <input 
                        type="checkbox" 
                        checked={isSelected} 
                        readOnly 
                        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6', flexShrink: 0 }} 
                      />
                    )}
                  </div>
                );
              })}
            </div>,
            document.body
          )
          : null
      )}

      {showDropdown && String(searchTerm || '').trim() && filteredItems.length === 0 && !showingAll && (
        <div className="reusable-dropdown-portal" style={{...baseDropdownStyle, position: 'absolute', top: '100%', left: 0, right: 0}}>
          <div style={{ padding: '20px 16px', textAlign: 'center', color: isDark ? 'rgba(203, 213, 225, 0.5)' : '#6b7280', fontSize: '14px' }}>
            {emptyMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReusableMultiSelect;
